import ApiService from '../common/api.service'
import base32 from 'hi-base32'

class VehicleService extends ApiService {

  constructor ($log, $http, $window, $rootScope, Localization, I18n, $state, $sce) {
    'ngInject'
    super($log, $http, $window, $rootScope, Localization, I18n)

    this.$sce = $sce

    $rootScope.$on('user:logout', (event, data) => {
      this._fetchingCurrentUserVehicles = null
      this._synchronizeCurrentUserVehicles = null
    })
  }

  /**
   * Synchronize Store / CSDB vehicles
   * @return {Promise} Promise of the current user vehicles
   */
  synchronizeCurrentUserVehicles () {
    if (this._synchronizeCurrentUserVehicles) {
      this.$log.debug('Already synchronizing current user vehicles. Waiting current resolution.')
      return this._synchronizeCurrentUserVehicles
    }
    this._synchronizeCurrentUserVehicles = this.put('/users/current/vehicles/csdb/synchronize')
      .then((res) => {
        this._synchronizeCurrentUserVehicles = null
        this.$log.info('Synchronized current user vehicles', res.data)
        return Promise.resolve(res.data)
      })
    return this._synchronizeCurrentUserVehicles
  }

  /**
   * Get vehicles of the current user.
   * @param {boolean} useCache Try to get vehicles form cache
   * @return {Promise} Promise of the current user vehicles
   */
  getCurrentUserVehicles (useCache = true) {
    const cacheKey = 'current_vehicles'
    const inCache = this.getFromCache(cacheKey)
    if (useCache && inCache) {
      // Get current vehicles form cache
      return Promise.resolve(inCache)
    }
    if (this._fetchingCurrentUserVehicles) {
      this.$log.debug('Already fetching current user vehicles. Waiting current resolution.')
      return this._fetchingCurrentUserVehicles
    }
    this._fetchingCurrentUserVehicles = this.get('/users/current/vehicles')
      .then((res) => {
        this.putInCache(cacheKey, res.data)
        this._fetchingCurrentUserVehicles = null
        this.$log.info('Fetched current user vehicles', res.data)
        return Promise.resolve(res.data)
      })
    return this._fetchingCurrentUserVehicles
  }

  /**
   * Get current user selected vehicle.
   * @param {boolean} useCache Try to get vehicles form cache
   * @return {Promise} Promise of the current user vehicles
   */
  getCurrentSelectedVehicle (useCache = true) {
    return this.getCurrentUserVehicles(useCache)
      .then((vehicles) => {
        if (vehicles.length === 0) {
          return Promise.resolve(null)
        }
        const { vin } = this.$window.sessionStorage
        var vehicle = vin ? vehicles.find((v) => v.vin === vin) : vehicles[0]
        if (!vehicle) {
          vehicle = vehicles[0]
          this.$log.info('Switch to vehicle:', vehicle)
          this.$window.sessionStorage.vin = vin
        }
        this.$log.info('Fetched current user selected vehicle', vehicle)
        return Promise.resolve(vehicle)
      })
  }

  /**
   * Set the current selected vehicle.
   * @param {string} vin VIN to select
   * @param {boolean} useCache Try to get vehicles form cache
   * @return {Promise} Promise of the current selected vehicle
   */
  setCurrentSelectedVehicle (vin, useCache = true) {
    return this.getCurrentUserVehicles(useCache)
      .then((vehicles) => {
        if (vehicles.length === 0) {
          return Promise.reject()
        }
        const vehicle = vin ? vehicles.find((v) => v.vin === vin) : vehicles[0]
        if (vehicle) {
          this.$log.info('Switch to vehicle:', vehicle)
          this.$window.sessionStorage.vin = vin
          return Promise.resolve(vehicle)
        } else {
          this.$log.error('Unable to switch to vehicle:', vin)
          return Promise.reject()
        }
      })
  }

  getCurrentVin () {
    const vin = this.$window.sessionStorage.vin
    return vin
  }

  /**
   * Get vehicle details.
   * @param {string} vin VIN
   * @return {Promise} Promise of the vehicle details
   */
  getVehicleDetails (vin, useCache = true) {
    const cacheKey = `v${vin}`
    const inCache = this.getFromCache(cacheKey)
    if (useCache && inCache) {
      // Get vehicle details form cache
      return Promise.resolve(inCache)
    }
    return this.get(`/vehicles/${vin}`)
      .then((res) => {
        this.putInCache(cacheKey, res.data)
        this.$log.info('Fetched vehicle', res.data)
        return Promise.resolve(res.data)
      })
  }

  /**
   * Add vehicle to the user garage.
   * @param {Object} vehicle Vehicle payload
   * @return {Promise} Promise of the vehicle details
   */
  addVehicle (vehicle) {
    return this.post('/users/current/vehicles', null, {
      params: {
        'vin': vehicle.vin,
        'name': vehicle.friendlyName
      }
    }).then((res) => {
      this.$log.info('Vehicle added', res.data)
      return Promise.resolve(res.data)
    })
  }

  /**
   * Edit vehicle of the user garage.
   * @param {Object} vehicle Vehicle to edit
   * @return {Promise} Promise of the vehicle details
   */
  editVehicle (vehicle) {
    this.$log.debug('Updating vehicle', vehicle)
    return this.put(`/vehicles/${vehicle.vin}`, null, {
      params: {
        'name': vehicle.name
      }
    }).then((res) => {
      this.$log.info('Vehicle edited', res.data)
      return Promise.resolve(res.data)
    })
  }

  /**
   * Remove vehicle from the user garage.
   * @param {Object} vehicle Vehicle to edit
   * @return {Promise} Promise of the vehicle details
   */
  deleteVehicle (vin) {
    return this.delete(`/users/current/vehicles/${vin}`)
    .then((res) => {
      this.$log.info('Vehicle deleted', res.data)
      return Promise.resolve(res.data)
    })
  }

  checkVin (vin) {
    return this.get(`/public/vehicles/${vin}`)
    .then((res) => Promise.resolve(res.data))
  }

  getVehicles (email) {
    return this.get(`/users/${email}/vehicles`)
    .then((res) => Promise.resolve(res.data))
  }

  /**
   * Change GDC Activation Display.
   * @param {Object} vehicle Vehicle to edit
   * @return {Promise} Promise of the vehicle details
   */
  changeDisplayGdcActivation (vin) {
    this.$log.debug('Updating vehicle', vin)
    return this.get(`/vehicles/${vin}/changeDisplayGdcActivation`)
    .then((res) => {
      this.$log.info('Display GDC activation changed', res.data)
    })
  }

  /**
   * Get message to alert new map to install
   * @param {Object} vin VIN of the vehicle
   * @return {Promise} Promise of the message
   */
  getMapAlertMessage (vin) {
    return this.get(`/vehicles/${vin}/getMapAlertMessage`)
    .then((res) => {
      return Promise.resolve(res.data)
    })
  }

  /**
   * Mark alert message as read
   * @param {string} vin VIN of the vehicle
   * @param {string} idList list of read messages ID
   * @return {Promise} Promise
   */
  markAlertMessageRead (vin, idList) {
    return this.put(`/vehicles/${vin}/markAlertMessageRead`, null, {
      params: {
        'idList': idList
      }
    })
    .then(() => {
      Promise.resolve()
    })
  }

  /**
   * Get products on vehicle devices
   * @param (string} vin - Identifier of vehicle to delete
   * @return {Promise} devices
   */
  getProductsOnDevices (vin) {
    this.$log.debug('Get products on devices for vehicle', vin)
    return this.get(`/vehicles/${vin}/devices`)
    .then((res) => {
      this.$log.debug('Products on devices', res.data)
      return Promise.resolve(res.data)
    })
  }

  /**
   * Synchronize vehicle device mounts
   * @param  {string} vin           - Identifier of vehicle
   * @param  {string} muid          - MUID of vehicle
   * @param  {string} mountId       - Mount id to synchronize
   * @param  {object} syncProducts  - Contains a List of Products to install and List of Products to delete
   * @return {Promise}
   */
  synchronizeDeviceMount (vin, muid, mountId, syncProducts) {
    this.$log.debug('Synchronize products for params', vin, muid, mountId, syncProducts)
    mountId = base32.encode(mountId)
    return this.post(`/vehicles/${vin}/devices/${muid}/mounts/${mountId}/synchronize`, syncProducts)
    .then((res) => {
      this.$log.debug('Device synchronized', res.data)
      return Promise.resolve()
    })
  }

  /**
   * Synchronize vehicle information and rights
   * @param  {string} vin           - Identifier of vehicle
   * @return {Promise}
   */
  synchronizeVehicleInfoAndRights (vin) {
    this.$log.debug('Synchronize info and rights for vin', vin)
    if (this._synchronizeCurrentUserVehicles) {
      this.$log.debug('Already synchronizing current user vehicles. Waiting current resolution.')
      return this._synchronizeCurrentUserVehicles
    } else {
      return this.put(`/vehicles/${vin}/synchronize`)
      .then((res) => {
        this.$log.debug('Vehicle info and rights synchronized', vin)
        return Promise.resolve()
      })
    }
  }

  /**
   * Synchronize user's vehicles information and rights
   * @return {Promise}
   */
  synchronizeVehiclesInfoAndRights () {
    this.$log.debug('Synchronize info and rights for all vehicles')
    return this.put(`/users/current/vehicles/synchronize`)
    .then((res) => {
      this.$log.debug('Vehicles info and rights synchronized')
      return Promise.resolve()
    })
  }
}

export default VehicleService



// WEBPACK FOOTER //
// ./src/app/services/vehicle/vehicle.service.js